import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api/client';
import { Banner, Spinner } from '../States';
import { useAuth } from '../../hooks/useAuth';
import { dateTime } from '../../lib/format';
import type { AdminOrderDetail, OrderStatus, SkinGroup } from '../../api/types';
import {
  ETAPAS,
  NOME_DO_TIPO,
  brl,
  linkWhatsapp,
  posicaoDaEtapa,
  proximoPasso,
  recadoParaCliente,
  rotuloCurto,
  textoParaCliente,
  valorDoPedido,
} from '../../lib/pedidos';

/**
 * O pedido aberto no painel, numa gaveta lateral.
 *
 * A ordem é a do trabalho de quem atende: em cima, em que etapa o pedido está
 * e o próximo passo; depois o que comprar e para quem entregar; embaixo o
 * histórico. A gaveta não tira a fila da tela: dá para fechar e abrir outro
 * pedido sem perder o lugar.
 */
export function PedidoDetalhe({
  code,
  steamId,
  onFechar,
  onMudou,
}: {
  code: string;
  steamId: string | null;
  onFechar: () => void;
  onMudou: () => void;
}) {
  const [dados, setDados] = useState<AdminOrderDetail | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** Depois de mudar a etapa, o painel oferece avisar o cliente. */
  const [mudouPara, setMudouPara] = useState<OrderStatus | null>(null);
  /** A nota que acompanha a mudança de etapa. Separada da nota solta do histórico. */
  const [notaPasso, setNotaPasso] = useState('');
  const [nota, setNota] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [copiado, setCopiado] = useState<string | null>(null);
  /** Edição dos itens: só aparece quando a equipe pede, para não virar clique sem querer. */
  const [editando, setEditando] = useState(false);
  const [valores, setValores] = useState<Record<number, string>>({});
  const [buscaItem, setBuscaItem] = useState('');
  const [achados, setAchados] = useState<SkinGroup[] | null>(null);
  const [erroEdicao, setErroEdicao] = useState<string | null>(null);
  const { papel, somenteLeitura } = useAuth();
  /** Editar é de quem decide: Master e Admin. O Desenvolvedor só olha. */
  const podeEditar = !somenteLeitura && (papel === 'master' || papel === 'admin');
  /** Concluído ou cancelado não muda mais: a API recusa, e o botão nem aparece. */
  const editavel = (status: OrderStatus) => status !== 'concluido' && status !== 'cancelado';
  const painel = useRef<HTMLElement>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.adminPedido(code));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui abrir o pedido.');
    }
  }, [code]);

  useEffect(() => {
    setDados(null);
    setMudouPara(null);
    setCancelando(false);
    setMotivo('');
    setNota('');
    setNotaPasso('');
    setEditando(false);
    setValores({});
    setBuscaItem('');
    setAchados(null);
    setErroEdicao(null);
    void carregar();
  }, [carregar]);

  // Esc fecha a gaveta; o foco entra nela para quem navega pelo teclado.
  useEffect(() => {
    painel.current?.focus();
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [onFechar]);

  /**
   * Toda edição recarrega o pedido e avisa a lista: o total muda, e o card do
   * quadro mostra valor. Erro fica na própria seção, não derruba a gaveta.
   */
  async function editar(acao: () => Promise<unknown>) {
    setOcupado(true);
    setErroEdicao(null);
    try {
      await acao();
      await carregar();
      onMudou();
    } catch (e) {
      setErroEdicao(e instanceof ApiError ? e.message : 'Não consegui salvar a alteração.');
    } finally {
      setOcupado(false);
    }
  }

  /** Busca no catálogo para acrescentar uma skin ao pedido. */
  useEffect(() => {
    const termo = buscaItem.trim();
    if (!editando || termo.length < 3) {
      setAchados(null);
      return;
    }
    const t = window.setTimeout(() => {
      void api
        .skins({ search: termo, pageSize: 8, sort: 'onsale' })
        .then((r2) => setAchados(r2.items))
        .catch(() => setAchados([]));
    }, 400);
    return () => window.clearTimeout(t);
  }, [buscaItem, editando]);

  async function copiar(texto: string, rotulo: string) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(rotulo);
      window.setTimeout(() => setCopiado(null), 1800);
    } catch {
      setErro('O navegador não deixou copiar. Selecione e copie à mão.');
    }
  }

  async function mudar(status: OrderStatus, opts: { motivo?: string; nota?: string } = {}) {
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.adminMudarStatusPedido(code, status, opts);
      setDados((d) => (d ? { ...d, order: r.order, eventos: r.eventos } : d));
      setMudouPara(status);
      setCancelando(false);
      setMotivo('');
      setNotaPasso('');
      onMudou();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui mudar a etapa.');
    } finally {
      setOcupado(false);
    }
  }

  async function anotar() {
    if (!nota.trim()) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.adminNotaPedido(code, nota.trim());
      setDados((d) => (d ? { ...d, eventos: r.eventos } : d));
      setNota('');
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar a nota.');
    } finally {
      setOcupado(false);
    }
  }

  const order = dados?.order;
  const proximo = order ? proximoPasso(order.kind, order.status) : null;
  const podeCancelar = order ? !['concluido', 'cancelado'].includes(order.status) : false;
  const linkConversa = linkWhatsapp(order?.whatsapp);
  const linkAvisar = order ? linkWhatsapp(order.whatsapp, recadoParaCliente(order)) : null;
  const saida = order?.items.filter((i) => i.side === 'saida') ?? [];
  const entrada = order?.items.filter((i) => i.side === 'entrada') ?? [];

  return (
    <>
      <div className="gp-veu" onClick={onFechar} aria-hidden="true" />
      <aside className="gp-gaveta" ref={painel} tabIndex={-1} role="dialog" aria-label={`Pedido ${code}`}>
        <header className="gp-gaveta-topo">
          <div>
            <span className="gp-codigo">{code}</span>
            {order && (
              <div className="gp-gaveta-sub">
                <span className={`gp-selo ${order.status}`}>{rotuloCurto(order.kind, order.status)}</span>
                <span className="muted">{NOME_DO_TIPO[order.kind]}</span>
                <span className="muted">aberto em {dateTime(order.createdAt)}</span>
              </div>
            )}
          </div>
          <button className="btn ghost small" onClick={onFechar} aria-label="Fechar o pedido">
            Fechar
          </button>
        </header>

        {erro && <Banner kind="error">{erro}</Banner>}
        {!dados && !erro && <Spinner label="Abrindo o pedido…" />}

        {order && dados && (
          <div className="gp-gaveta-corpo">
            {/* ── Etapa e próximo passo ─────────────────────────────────── */}
            {order.status === 'cancelado' ? (
              <Banner kind="error">Cancelado{order.cancelReason ? `: ${order.cancelReason}` : '.'}</Banner>
            ) : (
              <ol className="gp-etapas">
                {ETAPAS.map((s, i) => {
                  const pos = posicaoDaEtapa(order.status);
                  const estado = i + 1 < pos ? 'feita' : i + 1 === pos ? 'atual' : 'futura';
                  const quando = [...dados.eventos].reverse().find((e) => e.kind === 'status' && e.status === s);
                  return (
                    <li key={s} className={estado}>
                      <b>{textoParaCliente(order.kind, s).titulo}</b>
                      <span className="muted">{quando && estado !== 'futura' ? dateTime(quando.createdAt) : '—'}</span>
                    </li>
                  );
                })}
              </ol>
            )}

            {mudouPara && (
              <div className="gp-aviso-cliente">
                <span>
                  Pedido movido para <b>{rotuloCurto(order.kind, mudouPara)}</b>.{' '}
                  {linkAvisar ? 'Avise o cliente pelo WhatsApp dele:' : 'O cliente não informou um WhatsApp válido.'}
                </span>
                {linkAvisar && (
                  <a className="btn primary small" href={linkAvisar} target="_blank" rel="noreferrer noopener">
                    Avisar o cliente
                  </a>
                )}
              </div>
            )}

            {(proximo || podeCancelar) && !cancelando && (
              <div className="gp-acao">
                {proximo && (
                  <>
                    <input
                      id={`gp-nota-passo-${code}`}
                      className="input"
                      placeholder="Nota da mudança (opcional, só a equipe vê)"
                      value={notaPasso}
                      onChange={(e) => setNotaPasso(e.target.value)}
                    />
                    <button
                      className="btn primary"
                      onClick={() => void mudar(proximo.status, { nota: notaPasso.trim() || undefined })}
                      disabled={ocupado}
                    >
                      {proximo.acao}
                    </button>
                  </>
                )}
                {podeCancelar && (
                  <button className="btn ghost gp-perigo" onClick={() => setCancelando(true)} disabled={ocupado}>
                    Cancelar
                  </button>
                )}
              </div>
            )}

            {/* Cancelar pede motivo, e o motivo aparece para o cliente: por isso
                é um passo à parte, e não um prompt do navegador. */}
            {cancelando && (
              <div className="gp-cancelar">
                <label className="field" htmlFor={`gp-motivo-${code}`}>
                  <span>Motivo do cancelamento — o cliente vai ver</span>
                  <input
                    id={`gp-motivo-${code}`}
                    className="input"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Ex.: a peça foi vendida antes da compra"
                    autoFocus
                  />
                </label>
                <div className="actions-row">
                  <button
                    className="btn primary gp-perigo-cheio"
                    onClick={() => void mudar('cancelado', { motivo: motivo.trim() })}
                    disabled={ocupado || !motivo.trim()}
                  >
                    Confirmar cancelamento
                  </button>
                  <button className="btn ghost" onClick={() => setCancelando(false)} disabled={ocupado}>
                    Voltar
                  </button>
                </div>
              </div>
            )}

            {/* ── Cliente e entrega ─────────────────────────────────────── */}
            <section className="gp-bloco">
              <h4>Cliente</h4>
              <dl className="gp-dados">
                <dt>Nome na Steam</dt>
                <dd>{order.personaName ?? '—'}</dd>
                {steamId && (
                  <>
                    <dt>SteamID</dt>
                    <dd>
                      <code>{steamId}</code>
                    </dd>
                  </>
                )}
                <dt>WhatsApp</dt>
                <dd>{order.whatsapp ?? <span className="muted">não informado</span>}</dd>
                <dt>Trade link</dt>
                <dd className="break">{order.tradeUrl ?? <span className="muted">não informado</span>}</dd>
              </dl>
              <div className="actions-row">
                {linkConversa && (
                  <a
                    className="btn ghost small"
                    href={linkConversa}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Conversar
                  </a>
                )}
                {linkAvisar && !mudouPara && (
                  <a className="btn ghost small" href={linkAvisar} target="_blank" rel="noreferrer noopener">
                    Enviar status ao cliente
                  </a>
                )}
                {order.tradeUrl && (
                  <>
                    <a className="btn ghost small" href={order.tradeUrl} target="_blank" rel="noreferrer noopener">
                      Abrir trade link
                    </a>
                    <button className="btn ghost small" onClick={() => void copiar(order.tradeUrl!, 'trade')}>
                      {copiado === 'trade' ? 'Copiado' : 'Copiar trade link'}
                    </button>
                  </>
                )}
              </div>
            </section>

            {/* ── O que comprar ─────────────────────────────────────────── */}
            {dados.compra.length > 0 && (
              <section className="gp-bloco">
                <div className="gp-bloco-topo">
                  <h4>Comprar no marketplace</h4>
                  <button
                    className="btn ghost small"
                    onClick={() =>
                      void copiar(
                        dados.compra
                          .map((c) =>
                            [`${c.name} | templateId ${c.templateId} | anúncio ${c.listingId ?? '-'}`, c.url]
                              .filter(Boolean)
                              .join('\n'),
                          )
                          .join('\n\n'),
                        'compra',
                      )
                    }
                  >
                    {copiado === 'compra' ? 'Copiado' : 'Copiar lista'}
                  </button>
                </div>
                <p className="muted gp-dica">
                  O anúncio que o cliente escolheu. A peça é única: outra do mesmo desgaste tem float e pattern
                  diferentes.
                </p>
                <ul className="gp-compra">
                  {dados.compra.map((c, n) => {
                    const item = saida[n];
                    return (
                      <li key={n}>
                        {item?.iconUrl ? <img src={item.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                        <div>
                          <b>{c.name}</b>
                          <span className="muted">
                            {c.qty > 1 ? `${c.qty}× · ` : ''}
                            {c.listingId ? `anúncio ${c.listingId}` : 'sem anúncio específico'}
                          </span>
                        </div>
                        {c.url ? (
                          <a className="btn primary small" href={c.url} target="_blank" rel="noreferrer noopener">
                            {c.abreNoApp ? 'Abrir no app' : 'Abrir lista'}
                          </a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* ── Valores ───────────────────────────────────────────────── */}
            <section className="gp-bloco">
              <div className="gp-bloco-topo">
                <h4>Itens e valores</h4>
                {podeEditar && editavel(order.status) && (
                  <button className="btn ghost small" onClick={() => setEditando((v) => !v)} disabled={ocupado}>
                    {editando ? 'Concluir edição' : 'Editar itens'}
                  </button>
                )}
              </div>
              {erroEdicao && <Banner kind="error">{erroEdicao}</Banner>}
              <div className="table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Valor</th>
                      <th>Custo</th>
                      <th>Lucro</th>
                      {editando && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {[...saida, ...entrada].map((i, n) => (
                      <tr key={i.id ?? n}>
                        <td>
                          {i.side === 'entrada' && <span className="gp-lado">recebe</span>}
                          {i.name}
                          {i.qty > 1 ? ` ×${i.qty}` : ''}
                        </td>
                        <td>
                          {editando && i.id !== undefined ? (
                            // O valor digitado é o unitário; o total sai de valor × quantidade,
                            // como no fechamento.
                            <input
                              className="input gp-valor"
                              inputMode="decimal"
                              aria-label={`Valor unitário de ${i.name}`}
                              value={valores[i.id] ?? String(i.unitBrl).replace('.', ',')}
                              disabled={ocupado}
                              onChange={(e) => setValores((v) => ({ ...v, [i.id!]: e.target.value }))}
                              onBlur={() => {
                                const bruto = valores[i.id!];
                                if (bruto === undefined) return;
                                const novo = Number(bruto.replace(/\./g, '').replace(',', '.'));
                                setValores((v) => {
                                  const { [i.id!]: _fora, ...resto } = v;
                                  return resto;
                                });
                                if (!Number.isFinite(novo) || novo < 0 || novo === i.unitBrl) return;
                                void editar(() => api.adminAlterarItemDoPedido(code, i.id!, { unitBrl: novo }));
                              }}
                            />
                          ) : (
                            <b>{brl(i.totalBrl)}</b>
                          )}
                        </td>
                        <td className="dim">{i.costBrl === null ? '—' : brl(i.costBrl)}</td>
                        <td>{i.profitBrl === null ? <span className="dim">—</span> : <b className="pos">{brl(i.profitBrl)}</b>}</td>
                        {editando && (
                          <td>
                            {i.id !== undefined && order.items.length > 1 && (
                              <button
                                className="btn ghost small"
                                disabled={ocupado}
                                onClick={() => {
                                  if (!window.confirm(`Tirar "${i.name}" do pedido?`)) return;
                                  void editar(() => api.adminRemoverItemDoPedido(code, i.id!));
                                }}
                              >
                                Remover
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {editando && order.kind !== 'venda' && (
                <div className="gp-adicionar">
                  <label className="field">
                    <span>Acrescentar skin ao pedido</span>
                    <input
                      className="input"
                      placeholder="Busque pelo nome: AK-47 Redline…"
                      value={buscaItem}
                      disabled={ocupado}
                      onChange={(e) => setBuscaItem(e.target.value)}
                    />
                  </label>
                  {achados && achados.length === 0 && <span className="dim">Nada encontrado.</span>}
                  {achados && achados.length > 0 && (
                    <ul className="gp-achados">
                      {achados.map((g) => (
                        <li key={g.key}>
                          <b>{g.name}</b>
                          <span className="gp-desgastes">
                            {g.variants
                              .filter((v) => v.price !== null)
                              .map((v) => (
                                <button
                                  key={v.id}
                                  className="btn ghost small"
                                  disabled={ocupado}
                                  title={`Adicionar ${v.exterior?.pt ?? 'esta peça'}`}
                                  onClick={() =>
                                    void editar(() => api.adminAdicionarItemAoPedido(code, { templateId: v.id })).then(
                                      () => {
                                        setBuscaItem('');
                                        setAchados(null);
                                      },
                                    )
                                  }
                                >
                                  {v.exterior?.pt ?? 'Adicionar'}
                                </button>
                              ))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <small className="dim">
                    O preço entra pela mesma cotação da sacola. Depois dá para ajustar o valor na tabela acima.
                  </small>
                </div>
              )}

              <div className="gp-totais">
                <span>
                  {order.kind === 'venda' ? 'A pagar ao cliente' : order.kind === 'upgrade' ? 'A receber' : 'Total'}{' '}
                  <b>{brl(valorDoPedido(order))}</b>
                </span>
                {order.profitBrl !== null && (
                  <span>
                    Lucro <b className="pos">{brl(order.profitBrl)}</b>
                  </span>
                )}
              </div>
              {order.note && <p className="gp-obs">Observação do cliente: {order.note}</p>}
            </section>

            {/* ── Histórico e notas ─────────────────────────────────────── */}
            <section className="gp-bloco">
              <div className="gp-bloco-topo">
                <h4>Histórico</h4>
                <button className="btn ghost small" onClick={() => void copiar(dados.mensagem, 'resumo')}>
                  {copiado === 'resumo' ? 'Copiado' : 'Copiar resumo do pedido'}
                </button>
              </div>
              <div className="gp-nota-nova">
                <input
                  id={`gp-nota-${code}`}
                  className="input"
                  placeholder="Anotar algo sobre o pedido (só a equipe vê)"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void anotar();
                  }}
                />
                <button className="btn ghost" onClick={() => void anotar()} disabled={ocupado || !nota.trim()}>
                  Anotar
                </button>
              </div>
              <ol className="gp-historico">
                {[...dados.eventos].reverse().map((e, n) => (
                  <li key={n} className={e.kind}>
                    <span className="gp-historico-marca" aria-hidden="true" />
                    <div>
                      {e.kind === 'nota' ? (
                        <b>Nota</b>
                      ) : (
                        <b>{rotuloCurto(order.kind, e.status as OrderStatus)}</b>
                      )}
                      {e.note && <p>{e.note}</p>}
                      <span className="muted">
                        {dateTime(e.createdAt)}
                        {e.quem ? ` · ${e.quem}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}
      </aside>
    </>
  );
}
